import { ApiOnboardingStep, ApiOnboardingType } from "@incendium/api";
import {
  EcomPlatformOnboardingConfig,
  EcomPlatformOnboardingStepSlug,
} from "features/onboarding/ecomPlatform/types";

export const ECOM_PLATFORM_ONBOARDING_STEP_SLUGS: EcomPlatformOnboardingStepSlug[] =
  [
    "connect",
    "install-plugin",
    "scan",
    "pixel",
    "locations",
    "connections",
  ];

const SHARED_LEFT_PANEL: EcomPlatformOnboardingConfig["leftPanel"] = {
  [ApiOnboardingStep.LOCATIONS]: {
    heading: "Set your",
    headingAccent: "page groupings",
    paragraphs: [
      "Locations turn your pages into meaningful groups: product pages, categories, blogs, checkout flows. It's how Incendium learns your structure so it can map journeys, detect drop-offs, and surface insights fast.",
    ],
  },
  [ApiOnboardingStep.CONNECTIONS]: {
    heading: "Connect your",
    headingAccent: "ad platforms",
    paragraphs: [
      "Connect your ad accounts so Incendium can match spend, clicks and campaign data with your store performance. This powers clearer attribution, cleaner reporting and faster insights.",
    ],
  },
};

const WOO_COMMERCE_CONFIG: EcomPlatformOnboardingConfig = {
  onboardingType: ApiOnboardingType.ONBOARDING_TYPE_WOO_COMMERCE,
  platformName: "WooCommerce",
  hasPluginSteps: true,
  firstStep: ApiOnboardingStep.BASIC_INFO,
  stepOrder: [
    ApiOnboardingStep.BASIC_INFO,
    ApiOnboardingStep.BUSINESS_INFO,
    ApiOnboardingStep.PLATFORM,
    ApiOnboardingStep.PIXEL,
    ApiOnboardingStep.LOCATIONS,
    ApiOnboardingStep.CONNECTIONS,
  ],
  totalSteps: 6,
  slugToApiStep: {
    connect: ApiOnboardingStep.BASIC_INFO,
    "install-plugin": ApiOnboardingStep.BUSINESS_INFO,
    scan: ApiOnboardingStep.PLATFORM,
    pixel: ApiOnboardingStep.PIXEL,
    locations: ApiOnboardingStep.LOCATIONS,
    connections: ApiOnboardingStep.CONNECTIONS,
  },
  apiStepToSlug: {
    [ApiOnboardingStep.BASIC_INFO]: "connect",
    [ApiOnboardingStep.BUSINESS_INFO]: "install-plugin",
    [ApiOnboardingStep.PLATFORM]: "scan",
    [ApiOnboardingStep.PIXEL]: "pixel",
    [ApiOnboardingStep.LOCATIONS]: "locations",
    [ApiOnboardingStep.CONNECTIONS]: "connections",
  },
  leftPanel: {
    ...SHARED_LEFT_PANEL,
    [ApiOnboardingStep.BASIC_INFO]: {
      heading: "Connect your",
      headingAccent: "store",
      paragraphs: [
        "Connect your WooCommerce store to Incendium to get started. We'll securely link your store so you can access your data, insights, and tools in one place.",
      ],
    },
    [ApiOnboardingStep.BUSINESS_INFO]: {
      heading: "Install the",
      headingAccent: "plugin",
      paragraphs: [
        "Install the Incendium plugin on your WooCommerce store to complete setup and start using Incendium.",
      ],
    },
    [ApiOnboardingStep.PLATFORM]: {
      heading: "Scanning",
      headingAccent: "Your Store",
      paragraphs: [
        "Incendium is scanning your WooCommerce store and captured key data about your products, customers, and transactions.",
        "This data powers your AI agents and analytics setup. Review and refine your brand description below before continuing.",
      ],
    },
    [ApiOnboardingStep.PIXEL]: {
      heading: "Your events &",
      headingAccent: "conversions",
      paragraphs: [
        "Incendium has automatically configured key interactions and conversion signals for your WooCommerce store.",
        "These signals feed your custom Engagement Rate metric (called 'Effectiveness'), so you can measure meaningful engagement across your website.",
      ],
    },
  },
};

const SHOPIFY_CONFIG: EcomPlatformOnboardingConfig = {
  onboardingType: ApiOnboardingType.ONBOARDING_TYPE_SHOPIFY,
  platformName: "Shopify",
  hasPluginSteps: false,
  firstStep: ApiOnboardingStep.PLATFORM,
  stepOrder: [
    ApiOnboardingStep.PLATFORM,
    ApiOnboardingStep.PIXEL,
    ApiOnboardingStep.LOCATIONS,
    ApiOnboardingStep.CONNECTIONS,
  ],
  totalSteps: 4,
  slugToApiStep: {
    scan: ApiOnboardingStep.PLATFORM,
    pixel: ApiOnboardingStep.PIXEL,
    locations: ApiOnboardingStep.LOCATIONS,
    connections: ApiOnboardingStep.CONNECTIONS,
  },
  apiStepToSlug: {
    [ApiOnboardingStep.PLATFORM]: "scan",
    [ApiOnboardingStep.PIXEL]: "pixel",
    [ApiOnboardingStep.LOCATIONS]: "locations",
    [ApiOnboardingStep.CONNECTIONS]: "connections",
  },
  leftPanel: {
    ...SHARED_LEFT_PANEL,
    [ApiOnboardingStep.PLATFORM]: {
      heading: "Scanning",
      headingAccent: "Your Store",
      paragraphs: [
        "Incendium is scanning your Shopify store and captured key data about your products, customers, and transactions.",
        "This data powers your AI agents and analytics setup. Review and refine your brand description below before continuing.",
      ],
    },
    [ApiOnboardingStep.PIXEL]: {
      heading: "Your events &",
      headingAccent: "conversions",
      paragraphs: [
        "Incendium has automatically configured key interactions and conversion signals for your Shopify store.",
        "These signals feed your custom Engagement Rate metric (called 'Effectiveness'), so you can measure meaningful engagement across your website.",
      ],
    },
  },
};

const ECOM_PLATFORM_CONFIGS: Partial<
  Record<ApiOnboardingType, EcomPlatformOnboardingConfig>
> = {
  [ApiOnboardingType.ONBOARDING_TYPE_WOO_COMMERCE]: WOO_COMMERCE_CONFIG,
  [ApiOnboardingType.ONBOARDING_TYPE_SHOPIFY]: SHOPIFY_CONFIG,
};

const API_ONBOARDING_STEPS = new Set<string>(Object.values(ApiOnboardingStep));

export function isEcomPlatformOnboardingType(
  onboardingType?: ApiOnboardingType,
): onboardingType is
  | ApiOnboardingType.ONBOARDING_TYPE_WOO_COMMERCE
  | ApiOnboardingType.ONBOARDING_TYPE_SHOPIFY {
  return (
    onboardingType === ApiOnboardingType.ONBOARDING_TYPE_WOO_COMMERCE ||
    onboardingType === ApiOnboardingType.ONBOARDING_TYPE_SHOPIFY
  );
}

export function getEcomPlatformConfig(
  onboardingType: ApiOnboardingType,
): EcomPlatformOnboardingConfig | null {
  return ECOM_PLATFORM_CONFIGS[onboardingType] ?? null;
}

export function isEcomPlatformOnboardingStepSlug(
  value: string,
): value is EcomPlatformOnboardingStepSlug {
  return (ECOM_PLATFORM_ONBOARDING_STEP_SLUGS as string[]).includes(value);
}

export function normalizeEcomPlatformStep(
  config: EcomPlatformOnboardingConfig,
  step: ApiOnboardingStep,
): ApiOnboardingStep {
  if (config.stepOrder.includes(step)) {
    return step;
  }
  return config.firstStep;
}

export function serializeEcomPlatformOnboardingStep(
  config: EcomPlatformOnboardingConfig,
  step: ApiOnboardingStep | null,
): string | null {
  if (!step) {
    return null;
  }
  const normalizedStep = normalizeEcomPlatformStep(config, step);
  return config.apiStepToSlug[normalizedStep] ?? null;
}

export function deserializeEcomPlatformOnboardingStep(
  config: EcomPlatformOnboardingConfig,
  value: string | null,
): ApiOnboardingStep | null {
  if (!value) {
    return null;
  }
  if (isEcomPlatformOnboardingStepSlug(value)) {
    const step = config.slugToApiStep[value];
    if (step) {
      return step;
    }
    if (!config.hasPluginSteps) {
      return config.firstStep;
    }
  }
  if (API_ONBOARDING_STEPS.has(value)) {
    return normalizeEcomPlatformStep(config, value as ApiOnboardingStep);
  }
  return null;
}

export function getEcomPlatformStepNumber(
  config: EcomPlatformOnboardingConfig,
  step: ApiOnboardingStep,
): number {
  const normalizedStep = normalizeEcomPlatformStep(config, step);
  const idx = config.stepOrder.indexOf(normalizedStep);
  return idx >= 0 ? idx + 1 : 1;
}

export function getEcomPlatformNextOnboardingStep(
  config: EcomPlatformOnboardingConfig,
  step: ApiOnboardingStep,
): ApiOnboardingStep | null {
  const normalizedStep = normalizeEcomPlatformStep(config, step);
  const idx = config.stepOrder.indexOf(normalizedStep);
  if (idx < 0 || idx >= config.stepOrder.length - 1) {
    return null;
  }
  return config.stepOrder[idx + 1];
}

export function getEcomPlatformPrevOnboardingStep(
  config: EcomPlatformOnboardingConfig,
  step: ApiOnboardingStep,
): ApiOnboardingStep | null {
  const normalizedStep = normalizeEcomPlatformStep(config, step);
  const idx = config.stepOrder.indexOf(normalizedStep);
  if (idx <= 0) {
    return null;
  }
  return config.stepOrder[idx - 1];
}
